import {
  useCallback,
  useId,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { createPortal } from "react-dom";
import { useReducedMotion } from "../hooks/useReducedMotion.js";
import { useFocusTrap } from "../primitives/useFocusTrap.js";
import { cn } from "../lib/utils.js";
import { findTourTarget } from "./tour-targets.js";
import type { ApplicableStep } from "./tour-definition.js";

/**
 * The ONE tour renderer: dimmed backdrop, spotlight cut-outs over the real
 * targets, one compact card, progress, and Back / Next / Skip / Finish.
 *
 * ## Styling is NEUTRAL, deliberately and permanently
 *
 * There is NO `pb-accent`, `pb-purple` or `pb-iris` anywhere in this file, and
 * that is a product requirement rather than a taste: the accent means "this is
 * the action that commits this region" and the iris channel means "this is
 * Player AI" (ADR-0076). A tour commits nothing and is not Player AI, so
 * borrowing either colour would make the overlay argue with every real control
 * underneath it. `TourOverlay.test.tsx` asserts the absence.
 *
 * Neutral is expressed in TOKENS — `pb-overlay`, `pb-card-elevated`,
 * `pb-border`, `pb-text*` — not in raw `slate-*` classes. The approved mockup
 * is dark mode, and those tokens ARE that slate in dark mode while remaining
 * correct in light and high contrast. Hard-coding the palette would have made
 * the tour the one surface in the product that ignores the theme. The focus
 * ring stays `pb-teal`, which is the design system's focus treatment
 * everywhere and is not an accent CTA colour.
 *
 * The underlying product keeps its own colours — the spotlight is a hole in the
 * scrim, not a repaint.
 *
 * ## What it will not do
 *
 * It never navigates, never clicks the thing it is pointing at, and never
 * writes domain data. It explains and moves on; a step that wants the user to
 * press Submit highlights Submit and says so.
 */

export interface TourOverlayProps {
  /** The APPLICABLE steps, already filtered for targets and capabilities. */
  readonly steps: readonly ApplicableStep[];
  readonly index: number;
  readonly onIndexChange: (index: number) => void;
  /** Reached Finish on the last applicable step. */
  readonly onFinish: () => void;
  /** Skip, the close control, or Escape — all one outcome, by design. */
  readonly onSkip: () => void;
  /** Accessible name for the dialog, e.g. "Home tour". */
  readonly label: string;
}

interface Rect {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Where the overlay's two positioned things go, as ONE value.
 *
 * The card's placement is derived from the spotlight rects, so holding them in
 * two `useState`s meant a measurement set the rects, and a second layout effect
 * watching them set the card — a whole extra render pass between layout and
 * paint, and a chained effect whose only input was the previous effect's
 * output. One measurement produces one value.
 */
interface Placement {
  readonly rects: readonly Rect[];
  readonly pointer?: { top: number; left: number };
  readonly card: { readonly top: number; readonly left: number } | null;
}

const NO_PLACEMENT: Placement = { rects: [], card: null };

const SPOTLIGHT_PADDING = 6;
const CARD_GAP = 12;
const CARD_WIDTH = 320;
/** Keeps the card off the very edge of the viewport at any placement. */
const VIEWPORT_MARGIN = 12;
/** Used only before the card has ever been measured. */
const ASSUMED_CARD_HEIGHT = 180;

function rectOf(element: Element): Rect {
  const box = element.getBoundingClientRect();
  return {
    top: box.top - SPOTLIGHT_PADDING,
    left: box.left - SPOTLIGHT_PADDING,
    width: box.width + SPOTLIGHT_PADDING * 2,
    height: box.height + SPOTLIGHT_PADDING * 2,
  };
}

function unionRect(rects: readonly Rect[]): Rect | null {
  if (rects.length === 0) return null;
  const top = Math.min(...rects.map((r) => r.top));
  const left = Math.min(...rects.map((r) => r.left));
  const bottom = Math.max(...rects.map((r) => r.top + r.height));
  const right = Math.max(...rects.map((r) => r.left + r.width));
  return { top, left, width: right - left, height: bottom - top };
}

/**
 * Place the card so it does not cover what it is describing.
 *
 * Below the spotlight when there is room, above when there is not, and clamped
 * into the viewport either way. A card that lands on top of the control it is
 * explaining is the single most common way this kind of overlay goes wrong.
 */
function placeCard(
  anchor: Rect,
  cardHeight: number,
  viewport: { width: number; height: number },
  preferred: ApplicableStep["step"]["placement"],
): Pick<Placement, "card" | "pointer"> {
  const width = Math.min(CARD_WIDTH, viewport.width - 2 * VIEWPORT_MARGIN);
  const clampX = (x: number) =>
    Math.max(
      VIEWPORT_MARGIN,
      Math.min(x, viewport.width - width - VIEWPORT_MARGIN),
    );
  const clampY = (y: number) =>
    Math.max(
      VIEWPORT_MARGIN,
      Math.min(y, viewport.height - cardHeight - VIEWPORT_MARGIN),
    );
  if (
    preferred === "inside-start" ||
    (preferred === "auto" &&
      anchor.width > viewport.width * 0.6 &&
      anchor.height > viewport.height * 0.6)
  ) {
    return {
      card: { left: clampX(anchor.left + 24), top: clampY(anchor.top + 64) },
    };
  }
  const positions = {
    bottom: {
      left: clampX(anchor.left + anchor.width / 2 - width / 2),
      top: anchor.top + anchor.height + CARD_GAP,
    },
    top: {
      left: clampX(anchor.left + anchor.width / 2 - width / 2),
      top: anchor.top - cardHeight - CARD_GAP,
    },
    start: {
      left: anchor.left - width - CARD_GAP,
      top: clampY(anchor.top + anchor.height / 2 - cardHeight / 2),
    },
    end: {
      left: anchor.left + anchor.width + CARD_GAP,
      top: clampY(anchor.top + anchor.height / 2 - cardHeight / 2),
    },
  };
  const order = [
    ...(preferred === "auto" ? [] : [preferred]),
    "bottom",
    "top",
    "end",
    "start",
  ] as (keyof typeof positions)[];
  const side = order.find((side) => {
    const p = positions[side];
    return (
      p.left >= VIEWPORT_MARGIN &&
      p.top >= VIEWPORT_MARGIN &&
      p.left + width <= viewport.width - VIEWPORT_MARGIN &&
      p.top + cardHeight <= viewport.height - VIEWPORT_MARGIN
    );
  });
  if (!side)
    return {
      card: { left: clampX(anchor.left + 24), top: clampY(anchor.top + 24) },
    };
  const card = positions[side];
  const pointer =
    side === "bottom" || side === "top"
      ? {
          left: Math.max(
            card.left + 16,
            Math.min(anchor.left + anchor.width / 2, card.left + width - 16),
          ),
          top: side === "bottom" ? card.top : card.top + cardHeight,
        }
      : {
          left: side === "end" ? card.left : card.left + width,
          top: Math.max(
            card.top + 16,
            Math.min(
              anchor.top + anchor.height / 2,
              card.top + cardHeight - 16,
            ),
          ),
        };
  return { card, pointer };
}

/**
 * One measurement pass: where the targets are, and where the card goes.
 *
 * A module-level function taking the card node, rather than a hook reading a
 * ref, because every input is the live DOM and none of it can be derived during
 * render. Keeping it out of the component is what lets the callers below be a
 * ref callback and an event listener instead of layout effects that set state.
 */
function measurePlacement(
  applicable: ApplicableStep,
  card: HTMLElement | null,
): Placement {
  const rects = applicable.targets
    .map((id) => findTourTarget(id))
    .filter((element): element is HTMLElement => element !== null)
    .map(rectOf)
    .map((rect) => {
      const left = Math.max(4, rect.left);
      const top = Math.max(4, rect.top);
      return {
        left,
        top,
        width: Math.max(
          0,
          Math.min(window.innerWidth - 4, rect.left + rect.width) - left,
        ),
        height: Math.max(
          0,
          Math.min(window.innerHeight - 4, rect.top + rect.height) - top,
        ),
      };
    })
    .filter((rect) => rect.width > 0 && rect.height > 0);
  const anchorElement = applicable.step.anchor
    ? findTourTarget(applicable.step.anchor)
    : null;
  const anchor = anchorElement ? rectOf(anchorElement) : unionRect(rects);
  if (!anchor) return { rects, card: null };
  const height = card?.getBoundingClientRect().height ?? ASSUMED_CARD_HEIGHT;
  return {
    rects,
    ...placeCard(
      anchor,
      height,
      {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      applicable.step.placement,
    ),
  };
}

export function TourOverlay({
  steps,
  index,
  onIndexChange,
  onFinish,
  onSkip,
  label,
}: TourOverlayProps): ReactElement | null {
  const maskId = useId().replace(/:/g, "");
  const cardRef = useRef<HTMLDivElement | null>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const reducedMotion = useReducedMotion();
  const [placement, setPlacement] = useState<Placement>(NO_PLACEMENT);

  const current = steps[index];
  const total = steps.length;
  const isFirst = index === 0;
  const isLast = index === total - 1;

  // Focus lands on Next, so the keyboard path is Enter-through with Escape to
  // leave. Restoring focus on close is `useFocusTrap`'s own contract.
  useFocusTrap(cardRef, { active: true, initialFocusRef: nextRef });

  /**
   * Measure when the card is attached, and again whenever the STEP changes.
   *
   * A callback ref rather than a layout effect, and that is the whole point:
   * React invokes it in the commit phase with the node already in the DOM and
   * laid out, so the measurement is real and the state it produces is flushed
   * before the browser paints — the property the layout effect was here for (a
   * frame of full-screen scrim with no hole, or a card at the viewport centre
   * jumping to its anchor, both read as the page having glitched).
   *
   * Re-running per step needs no extra effect: the callback's identity changes
   * with `current`, so React detaches and re-attaches it on the SAME node,
   * which is a fresh measurement and nothing else. `useFocusTrap` reads
   * `cardRef` from its own effect and is unaffected.
   */
  const attachCard = useCallback(
    (node: HTMLDivElement | null) => {
      cardRef.current = node;
      if (!node || !current) return;
      setPlacement((previous) => {
        const next = measurePlacement(current, node);
        return JSON.stringify(previous) === JSON.stringify(next)
          ? previous
          : next;
      });
    },
    [current],
  );

  /** Re-measure in response to something moving. Never called during render. */
  const remeasure = useCallback(() => {
    if (!current) return;
    setPlacement((previous) => {
      const next = measurePlacement(current, cardRef.current);
      return JSON.stringify(previous) === JSON.stringify(next)
        ? previous
        : next;
    });
  }, [current]);

  useEffect(() => {
    // Scroll the first target into view once per step, then track it. `block:
    // "center"` keeps the card's own space usable above and below.
    if (current?.step.scrollIntoView === false) return;
    const first = current?.targets[0];
    const element = first ? findTourTarget(first) : null;
    element?.scrollIntoView({
      block: "center",
      inline: "nearest",
      behavior: reducedMotion ? "auto" : "smooth",
    });
  }, [current, reducedMotion]);

  useEffect(() => {
    // Scroll and resize both move a target under a fixed overlay, and so does
    // opening the Player AI pane — which is a layout change this component
    // cannot observe directly, so the capture-phase scroll listener (which sees
    // scrolls in any container) plus a ResizeObserver on the body is what keeps
    // the spotlight attached.
    //
    // This effect only REGISTERS listeners; `remeasure` runs from an event or
    // an observer notification, never synchronously from here.
    window.addEventListener("scroll", remeasure, true);
    window.addEventListener("resize", remeasure);
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(remeasure);
    observer?.observe(document.body);
    const targets = [
      ...(current?.targets ?? []),
      ...(current?.step.anchor ? [current.step.anchor] : []),
    ]
      .map((id) => findTourTarget(id))
      .filter((element): element is HTMLElement => element !== null);
    for (const element of targets) observer?.observe(element);
    if (cardRef.current) observer?.observe(cardRef.current);
    // Canvas pan/zoom uses transforms and need not resize any DOM node.
    let frame = 0;
    const track = () => {
      remeasure();
      frame = window.requestAnimationFrame(track);
    };
    const transformed = targets.some((element) => {
      for (
        let node: HTMLElement | null = element;
        node;
        node = node.parentElement
      ) {
        const transform = getComputedStyle(node).transform;
        if (transform && transform !== "none") return true;
      }
      return false;
    });
    if (transformed) frame = window.requestAnimationFrame(track);
    return () => {
      window.removeEventListener("scroll", remeasure, true);
      window.removeEventListener("resize", remeasure);
      observer?.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [current, remeasure]);

  const advance = useCallback(() => {
    if (isLast) onFinish();
    else onIndexChange(index + 1);
  }, [index, isLast, onFinish, onIndexChange]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      // Escape is SKIP, not "close and ask again tomorrow". Treating the close
      // control and Escape as anything softer than Skip is what makes a tour
      // reappear on every visit for somebody who has already dismissed it.
      if (event.key === "Escape") {
        event.preventDefault();
        onSkip();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onSkip]);

  const titleId = useMemo(
    () => `pb-tour-title-${current?.step.id ?? "none"}`,
    [current],
  );
  const bodyId = useMemo(
    () => `pb-tour-body-${current?.step.id ?? "none"}`,
    [current],
  );

  if (!current || typeof document === "undefined") return null;

  return createPortal(
    // Portal mounts after page popovers, keeping the tour above existing menus.
    <div className="fixed inset-0 z-popover" data-testid="pb-tour-overlay">
      {/* The scrim is the only thing that blocks pointer events. It is inert:
          clicking it does nothing, because a click-to-dismiss would make
          "I clicked slightly off the card" indistinguishable from Skip. */}
      <svg
        aria-hidden
        className="absolute inset-0 h-full w-full"
        data-testid="pb-tour-scrim"
      >
        <defs>
          <mask
            id={maskId}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="100%"
            height="100%"
          >
            <rect width="100%" height="100%" fill="white" />
            {placement.rects.map((rect, i) => (
              <rect
                key={i}
                x={rect.left}
                y={rect.top}
                width={rect.width}
                height={rect.height}
                rx="8"
                fill="black"
              />
            ))}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="hsl(var(--pb-overlay) / 0.65)"
          mask={`url(#${maskId})`}
        />
      </svg>
      {placement.rects.map((rect, rectIndex) => (
        <div
          key={rectIndex}
          aria-hidden
          data-testid="pb-tour-spotlight"
          className={cn(
            "absolute rounded-lg",
            "ring-2 ring-pb-text/70",
            reducedMotion
              ? ""
              : "transition-[top,left,width,height] duration-motion-state ease-motion-standard",
          )}
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        />
      ))}

      {placement.pointer ? (
        <div
          aria-hidden
          data-testid="pb-tour-pointer"
          className="absolute h-3 w-3 rotate-45 border border-pb-border bg-pb-card-elevated"
          style={{
            top: placement.pointer.top - 6,
            left: placement.pointer.left - 6,
          }}
        />
      ) : null}
      <div
        ref={attachCard}
        role="dialog"
        aria-modal="true"
        /* Named by the TOUR, described by the STEP.
           `aria-labelledby` and `aria-label` were both set, and labelledby wins
           outright — so the dialog was announced as whatever the current step's
           title happened to be, and its name changed under the reader on every
           Next. A dialog's name is its stable identity; here that is the tour.
           The step is CONTENT, so it rides `aria-describedby` and is announced
           on open and on each step change without renaming the dialog.
           This is why there is no `aria-labelledby`: the tour name is chrome
           and appears nowhere on the card, so there is no visible text to point
           at (unlike `ExpandedCardModal`, which has a visible header title). */
        aria-label={label}
        aria-describedby={`${titleId} ${bodyId}`}
        style={{
          width: CARD_WIDTH,
          maxWidth: "calc(100vw - 24px)",
          maxHeight: "calc(100vh - 24px)",
          overflowY: "auto",
          ...(placement.card
            ? { top: placement.card.top, left: placement.card.left }
            : {
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }),
        }}
        className="absolute rounded-lg border border-pb-border bg-pb-card-elevated p-4 text-pb-text shadow-xl"
      >
        {/* A real heading, not a styled paragraph: it is the step's title, so
            heading navigation should reach it. Level 2 because the overlay is a
            dialog rather than a page region — it has no h1 of its own. */}
        <h2
          id={titleId}
          className="mb-1 text-sm font-semibold tracking-tight text-pb-text"
        >
          {current.step.title}
        </h2>
        <p
          id={bodyId}
          className="mb-4 text-xs leading-relaxed text-pb-text-secondary"
        >
          {current.body}
        </p>
        <div className="flex items-center justify-between gap-3">
          <span
            className="text-xs text-pb-text-muted"
            // Announced on every step change, so a screen-reader user hears
            // where they are without hunting for the counter.
            role="status"
            aria-live="polite"
          >
            Step {index + 1} of {total}
          </span>
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={onSkip}
              className="rounded-md px-2 py-1 text-xs text-pb-text-muted hover:text-pb-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal"
            >
              Skip tour
            </button>
            {/* Omitted, not disabled, on the first step: a control that can
                never do anything is noise in the tab order. */}
            {isFirst ? null : (
              <button
                type="button"
                onClick={() => onIndexChange(index - 1)}
                className="rounded-md border border-pb-control-border px-2.5 py-1 text-xs text-pb-text hover:pb-overlay-paint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal"
              >
                Back
              </button>
            )}
            <button
              ref={nextRef}
              type="button"
              onClick={advance}
              /* The "light neutral primary button" of the approved design,
                 expressed with the theme's own neutral inversion rather than a
                 palette class — and deliberately NOT `bg-pb-accent`, which
                 would claim this button commits something. */
              className="rounded-md bg-pb-text px-2.5 py-1 text-xs font-medium text-pb-page hover:bg-pb-text/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal"
            >
              {isLast ? "Finish" : "Next"}
            </button>
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
